
import React from 'react';
import { Audience } from '@/store/videoFeatures';
import { useAudienceSimulation } from '@/hooks/use-audience-simulation';
import { cn } from '@/lib/utils';

interface ViewerCountProps {
  audience: Audience;
  forceShow?: boolean;
  autoplaySmallActive?: boolean;
}

export const ViewerCount: React.FC<ViewerCountProps> = ({
  audience,
  forceShow = false,
  autoplaySmallActive = false
}) => {
  const { formattedMessage } = useAudienceSimulation(audience);

  // If forceShow is true, always show for preview purposes
  if (!forceShow && !audience.enabled) return null;

  // Determine background color (with transparency support)
  const getBackgroundColor = () => {
    const bgColor = audience.style?.backgroundColor || '#000000';

    // Se já é rgba, usar diretamente
    if (bgColor.startsWith('rgba') || bgColor.startsWith('rgb')) {
      return bgColor;
    }

    // Se é hex e tem transparência configurada, converter para rgba
    if (audience.style?.useTransparency && audience.style?.opacity !== undefined) {
      const hex = bgColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${audience.style.opacity / 100})`;
    }

    return bgColor;
  };

  const textColor = audience.style?.textColor || '#ffffff';

  // Posicionamento baseado no autoplay pequeno
  const positionStyle = autoplaySmallActive
    ? { top: '5%', right: '3%', left: 'auto' } // Lado direito quando autoplay small ativo
    : { top: '0.5rem', left: '0.5rem', right: 'auto' }; // Lado esquerdo padrão

  return (
    <div
      className={cn(
        'absolute px-2.5 py-1.5 rounded-md flex items-center gap-2 live-indicator',
        'text-sm', // Tamanho fixo: médio
        'whitespace-nowrap max-w-[90%]'
      )}
      style={{
        backgroundColor: getBackgroundColor(),
        color: textColor,
        zIndex: 20, // Maior que o autoplay (15) para não ficar abaixo
        ...positionStyle
      }}
    >
      {/* Viewer Count */}
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 min-w-[8px] bg-red-600 rounded-full animate-pulse" />
        <span className="font-medium truncate">{formattedMessage}</span>
      </div>
    </div>
  );
};
